// User-facing text for a 429 from TED's rate limiter (domain/utilities/rate_limiter), which
// answers {"detail": "Rate limit exceeded.", "retry_after": <seconds>} plus a Retry-After header.
// Rate blocks escalate 5, 10, 20, 40, 80 min...; account-sharing blocks start at an hour. Past an
// hour the user is better off asking support than waiting.

export const SUPPORT_EMAIL = "support@ortex.com";
export const TERMS_URL = "https://public.ortex.com/terms-and-conditions";

const LONG_BLOCK_SECONDS = 60 * 60;

export interface RateLimitMessage {
    text: string;
    /** True when the block is long enough that the user should contact support. */
    contactSupport: boolean;
}

/** Seconds until the block lifts, from the 429 body or the Retry-After header; null if neither says. */
export const retryAfterSeconds = (data: unknown, headers?: unknown): number | null => {
    const fromBody = (data as { retry_after?: unknown } | null | undefined)?.retry_after;
    const fromHeader = (headers as Record<string, unknown> | null | undefined)?.["retry-after"];
    for (const value of [fromBody, fromHeader]) {
        const seconds = Number(value);
        if (value !== undefined && value !== null && value !== "" && Number.isFinite(seconds) && seconds > 0) return seconds;
    }
    return null;
};

const formatWait = (seconds: number): string => {
    const minutes = Math.max(1, Math.ceil(seconds / 60));
    if (minutes < 60) return minutes === 1 ? "1 minute" : `${minutes} minutes`;
    const hours = Math.round(minutes / 60);
    return hours === 1 ? "1 hour" : `${hours} hours`;
};

export const rateLimitMessage = (retryAfter: number | null): RateLimitMessage => {
    if (retryAfter === null) {
        return { text: "Too many requests. You have been temporarily rate limited: please wait a moment and try again.", contactSupport: false };
    }
    if (retryAfter > LONG_BLOCK_SECONDS) {
        return {
            text: `Your account has been blocked for about ${formatWait(retryAfter)} for a potential breach of our Terms and Conditions. Please contact ${SUPPORT_EMAIL} to restore access.`,
            contactSupport: true,
        };
    }
    return {
        text: `Your account is currently rate limited for a potential breach of our Terms and Conditions. You will regain access in about ${formatWait(retryAfter)}.`,
        contactSupport: false,
    };
};
